import {
  // FaExclamationTriangle,
  FaPhoneAlt,
  FaInfoCircle,
  FaHandsHelping,
  FaFileAlt,
} from 'react-icons/fa';
import { Seo, buildFAQSchema, buildWebPageSchema } from '../../core/seo';

const SAFETY_FAQS = [
  {
    question: 'Does HirePanther automatically insure bookings?',
    answer:
      'No. HirePanther does not automatically insure bookings. Coverage varies by provider and service type. Always ask your provider for proof of insurance for high-risk jobs.',
  },
  {
    question: 'What insurance should service providers carry on HirePanther?',
    answer:
      "Providers should carry Commercial General Liability (CGL) insurance of CAD $1M–$2M, Professional/Errors & Omissions insurance if applicable, Commercial Auto insurance if driving is required, and Workers' Compensation where required by province.",
  },
  {
    question: 'How do I report a safety issue or incident on HirePanther?',
    answer:
      'Ensure your safety first. Collect photos, screenshots, receipts, and a detailed description. Report within 72 hours to safety@hirepanther.ca or use the in-app report form. HirePanther will triage, acknowledge receipt, and assign a case number.',
  },
  {
    question: 'What happens after I report an issue?',
    answer:
      'HirePanther reviews all evidence and transaction data, then resolves via remediation, documentation for insurers, or enforcement actions such as account suspension or removal.',
  },
  {
    question: 'Is it safe to pay for services through HirePanther?',
    answer:
      'Yes. HirePanther uses secure payment processing with fraud and chargeback reviews. Funds are disbursed to providers only after the job is complete. You should never pay a provider off-platform.',
  },
  {
    question: 'What safety standards must providers follow?',
    answer:
      'Providers must maintain accurate identity information, behave professionally, comply with all applicable licenses and permits, carry appropriate insurance, follow health and safety codes, and never offer prohibited or illegal services.',
  },
];

const safetyFaqSchema = buildFAQSchema({
  questions: SAFETY_FAQS,
  url: 'https://uat.hirepanther.ca/safety-insurance',
});
const safetyWebPageSchema = buildWebPageSchema({
  name: 'Safety & Insurance | HirePanther',
  description:
    'Learn about safety standards and insurance at HirePanther. Report an issue, emergency guidance, and insurance FAQs.',
  url: 'https://uat.hirepanther.ca/safety-insurance',
  mainEntity: safetyFaqSchema,
});

const SafetyInsurance = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Safety & Insurance"
        description="Learn about safety standards and insurance at HirePanther. Report an issue, emergency guidance, and insurance FAQs."
        canonicalPath="/safety-insurance"
        structuredData={[safetyFaqSchema, safetyWebPageSchema]}
      />
      <div className="max-w-5xl mx-auto p-6 text-[#284F60]  bg-white">
        {/* ===== Top Summary Bar ===== */}
        <div className="bg-[#2D617A] text-white p-4 rounded-xl shadow-md mb-8 flex flex-col md:flex-row justify-around gap-4">
          {/*<a
            href="/report-issue"
            className="flex items-center gap-2 hover:underline"
          >
            <FaExclamationTriangle className="text-xl" />
            Report an Issue
          </a>*/}
          <a
            href="#emergency-guidance"
            className="flex items-left gap-2 hover:underline"
          >
            <FaPhoneAlt className="text-xl" />
            Emergency Guidance
          </a>
          <a
            href="#insurance-faqs"
            className="flex items-right gap-2 hover:underline"
          >
            <FaInfoCircle className="text-xl" />
            Insurance FAQs
          </a>
        </div>

        {/* ===== Page Title ===== */}
        <h1 className="text-3xl font-bold mb-6 text-[#284F60] flex items-center gap-3">
          Safety & Insurance at HirePanther
        </h1>

        {/* ===== Sections ===== */}
        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              1) Our Promise
            </h2>
            <p>
              Your safety is our priority. HirePanther is a Canadian marketplace
              that connects customers with independent service providers. We
              design our product, processes, and policies to help keep bookings
              safe, transparent, and fair for everyone.
            </p>
            <p className="mt-2 font-medium">
              Key pillars: verified profiles, secure payments, clear standards,
              and responsive support.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              2) Who We Are (and aren’t)
            </h2>
            <p>
              HirePanther operates a platform—we are not the employer of
              providers. Providers are independent businesses responsible for
              their own compliance (licenses, taxes, insurance). We set rules
              for using the platform and remove users who violate them.
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>
                <strong>Customers:</strong> book, pay securely, and review.
              </li>
              <li>
                <strong>Providers:</strong> list services, set availability &
                pricing, and deliver the work.
              </li>
              <li>
                <strong>HirePanther:</strong> facilitates discovery, booking,
                messaging, payment, and support.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              3) Provider Safety Standards
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Accurate identity & profile information</li>
              <li>Professional conduct and punctuality</li>
              <li>Legal compliance with permits and licenses</li>
              <li>Maintain appropriate insurance coverage</li>
              <li>Follow health, safety codes, and protect privacy</li>
              <li>No prohibited, illegal, or hazardous services</li>
            </ul>
            <p className="mt-2 text-[#284F60]">
              We may warn, suspend, or remove accounts for breaches, and
              cooperate with lawful requests from authorities.
            </p>
          </div>

          {/* ===== Insurance Overview with Callout Box ===== */}
          <div id="insurance-faqs">
            <h2 className="text-xl font-semibold text-[#2D617A] mb-4">
              4) Insurance Overview
            </h2>

            {/* Callout Box */}
            <div className="bg-yellow-100 border-l-4 border-yellow-400 p-4 rounded-lg shadow-sm mb-4">
              <h3 className="font-bold text-yellow-800 flex items-center gap-2">
                <FaFileAlt /> What’s Covered vs. Not Covered
              </h3>
              <p className="text-yellow-700 text-sm mt-1">
                HirePanther does not automatically insure bookings. Coverage
                varies by provider and service type. Always ask your provider
                for proof of insurance for high-risk jobs.
              </p>
            </div>

            <h3 className="font-medium-semibold text-[#2D617A]">
              4.1 What customers should know
            </h3>
            <p className="ml-4">
              Many providers carry Commercial General Liability (CGL) insurance
              that may cover property damage or third-party bodily injury.
              Coverage varies—ask providers for proof of insurance for high-risk
              jobs.
            </p>

            <h3 className="font-medium-semibold mt-3 text-[#2D617A]">
              4.2 What providers should carry
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Commercial General Liability (CGL): CAD $1M–$2M</li>
              <li>Professional/Errors & Omissions (if applicable)</li>
              <li>Commercial Auto (if required for service)</li>
              <li>Workers’ compensation (where required by province)</li>
            </ul>

            <h3 className="font-medium-semibold mt-3 text-[#2D617A]">
              4.3 Platform protections
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Secure payments with fraud/chargeback reviews</li>
              <li>Funds disbursed post-job per payout schedule</li>
            </ul>
          </div>

          {/* ===== Booking Safety Tips ===== */}
          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              5) Booking Safety Tips (Customers)
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                Check provider ratings, reviews, photos, and insurance notes
              </li>
              <li>
                Use in-app chat to confirm scope, materials, and instructions
              </li>
              <li>Ensure a responsible adult is present on service day</li>
              <li>Secure pets and valuables; clear work area</li>
              <li>Pay only via HirePanther—never cash/off-platform</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              6) Service Delivery Safety (Providers)
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Confirm job details via in-app chat</li>
              <li>Use appropriate PPE and follow codes</li>
              <li>Document high-risk jobs with before/after photos</li>
              <li>Never request off-platform payments</li>
            </ul>
          </div>

          {/* ===== Incident & Claims with Callout Box ===== */}
          <div id="report-issue">
            <h2 className="text-xl font-semibold text-[#2D617A] mb-4">
              7) Incident & Claims Process
            </h2>

            {/* Callout Box */}
            <div className="bg-blue-100 border-l-4 border-blue-500 p-4 rounded-lg shadow-sm mb-4">
              <h3 className="font-bold text-blue-700 flex items-center gap-2">
                <FaHandsHelping /> How to Report
              </h3>
              <p className="text-blue-700 text-sm mt-1">
                Ensure safety first. Collect photos, screenshots, receipts, and
                a detailed description. Report within 72 hours to{' '}
                <strong>
                  <a href="mailto:safety@hirepanther.ca">
                    safety@hirepanther.ca
                  </a>
                </strong>{' '}
                or use the form at <strong>/help/report</strong>.
              </p>
            </div>

            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>We triage, acknowledge receipt, and assign a case number</li>
              <li>Review all evidence and transaction data</li>
              <li>
                Resolve via remediation, documentation for insurers, or
                enforcement actions
              </li>
            </ul>
          </div>

          {/* Additional Sections */}
          <div id="emergency-guidance">
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              13) Contact
            </h2>
            <p>
              <strong>Urgent Safety:</strong> Call local emergency services
              first.
            </p>
            <p>
              <strong>Platform safety/claims:</strong>{' '}
              <a href="mailto:safety@hirepanther.ca">safety@hirepanther.ca</a>
            </p>
            <p>
              <strong>General support</strong>{' '}
              <a href="mailto:support@hirepanther.ca">
                {' '}
                support@hirepanther.ca
              </a>
            </p>
          </div>
        </section>

        {/* ===== Sticky Mobile Button ===== */}
        {/* <div className="fixed bottom-4 right-4 md:hidden">
          <a
            href="#report-issue"
            className="bg-red-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-red-700 transition-all flex items-center gap-2"
          >
            <FaExclamationTriangle /> Report Issue
          </a>
        </div> */}
      </div>
    </div>
  );
};

export default SafetyInsurance;
